import { useState, useEffect, useCallback, useRef, useMemo, memo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { writeupAPI } from "@/lib/api";
import { SITE, CREDENTIALS } from "@/lib/site";
import { slugify, isUuid, writeupPath } from "@/lib/slug";
import SEO from "@/components/SEO";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeSlug from "rehype-slug";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import {
  ArrowLeft, ArrowRight, Clock, Calendar, User,
  Share2, Copy, Check, ChevronRight,
  Mail, FileText, ArrowUpRight,
} from "lucide-react";

import TerminalBlock from "@/components/TerminalBlock";
import CodeBlock     from "@/components/CodeBlock";
import OutputBlock   from "@/components/OutputBlock";

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const SITE_ORIGIN = "https://www.maxoffsec.com";

const readingTime = (text = "") =>
  Math.max(1, Math.round(text.trim().split(/\s+/).length / 200));

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    // Locale is pinned, not inherited from the browser: a pt-BR visitor and an
    // en-AU visitor must see the same date, and it must match the cards.
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return ""; }
};

const formatPlatform = (platform) => {
  const labels = {
    htb: "Hack The Box",
    proving_grounds: "Proving Grounds",
    other: "Other"
  };
  return labels[platform] || platform || "Independent";
};

const formatOsLabel = (osType) => {
  if (!osType) return null;
  return osType.charAt(0).toUpperCase() + osType.slice(1);
};

const childrenToText = (ch) => {
  if (!ch) return "";
  if (typeof ch === "string") return ch;
  if (Array.isArray(ch)) return ch.map(childrenToText).join("");
  if (ch?.props?.children) return childrenToText(ch.props.children);
  return "";
};

const DIFF = {
  easy:   { label: "Easy",   color: "#00ff88", rgb: "0,255,136"  },
  medium: { label: "Medium", color: "#ff8c42", rgb: "255,140,66" },
  hard:   { label: "Hard",   color: "#ff4455", rgb: "255,68,85"  },
  insane: { label: "Insane", color: "#cc44ff", rgb: "204,68,255" },
};

const TAG_COLORS = {
  nmap:    { bg:"rgba(0,255,136,.12)",  border:"rgba(0,255,136,.38)",  color:"#00ff88"  },
  recon:   { bg:"rgba(0,255,136,.12)",  border:"rgba(0,255,136,.38)",  color:"#00ff88"  },
  xss:     { bg:"rgba(255,68,85,.12)",  border:"rgba(255,68,85,.4)",   color:"#ff4455"  },
  lfi:     { bg:"rgba(255,209,102,.1)", border:"rgba(255,209,102,.38)",color:"#ffbd2e"  },
  rce:     { bg:"rgba(255,68,85,.15)",  border:"rgba(255,68,85,.45)",  color:"#ff4455"  },
  sqli:    { bg:"rgba(255,140,66,.12)", border:"rgba(255,140,66,.4)",  color:"#ff8c42"  },
  privesc: { bg:"rgba(255,140,66,.12)", border:"rgba(255,140,66,.4)",  color:"#ff8c42"  },
  cve:     { bg:"rgba(204,68,255,.12)", border:"rgba(204,68,255,.38)", color:"#cc44ff"  },
  crypto:  { bg:"rgba(0,212,255,.1)",   border:"rgba(0,212,255,.38)",  color:"#00d4ff"  },
  ssrf:    { bg:"rgba(0,212,255,.1)",   border:"rgba(0,212,255,.38)",  color:"#00d4ff"  },
  default: { bg:"rgba(86,101,122,.1)",  border:"rgba(86,101,122,.35)", color:"#56657a"  },
};

/* ─────────────────────────────────────────
   SKILLS + TECHNIQUES DATA
───────────────────────────────────────── */
const SKILLS_DATA = [
  { label: "Web", cls: "t-web", tags: [
    "SQLi","Blind SQLi","Error-Based SQLi","Union SQLi","Time-Based SQLi",
    "XSS","Stored XSS","Reflected XSS","DOM XSS","SSTI","SSRF","Blind SSRF",
    "LFI","RFI","RCE","File Upload","Path Traversal","Open Redirect","IDOR","BOLA",
    "JWT Forgery","JWT None Algorithm","OAuth Abuse","CORS Bypass","Cookie Manipulation",
    "Session Fixation","CSRF","Clickjacking","HTTP Request Smuggling","WebSocket Hijack",
    "API Abuse","GraphQL Injection","Mass Assignment","Business Logic","Race Condition",
    "Type Juggling","PHP Object Injection","Prototype Pollution","Cache Poisoning",
    "Host Header Injection","HTTP Parameter Pollution","Subdomain Takeover",
    "2FA Bypass","WAF Bypass","Rate Limit Bypass","Broken Auth",
  ]},
  { label: "Injection", cls: "t-inject", tags: [
    "Command Injection","Blind CMDi","XXE","Blind XXE","XPath Injection",
    "LDAP Injection","NoSQLi","Log Poisoning","Log4Shell","Template Injection",
    "Deserialization","Java Deserialization",".NET Deserialization",
    "YAML Injection","EL Injection","Header Injection","HTML Injection","CSS Injection",
  ]},
  { label: "Linux PrivEsc", cls: "t-privl", tags: [
    "SUID Abuse","SGID Abuse","Sudo Abuse","Cron Abuse","Capabilities",
    "Wildcard Injection","PATH Hijacking","LD_PRELOAD","Writable /etc/passwd",
    "NFS No_root_squash","Docker Breakout","LXD/LXC Abuse","Kernel Exploit",
    "Dirty COW","Dirty Pipe","PwnKit","MySQL UDF","Python Lib Hijack",
    "Shared Object Injection","Systemd Abuse","D-Bus Abuse","Weak File Permissions","SUID Shell",
  ]},
  { label: "Win PrivEsc", cls: "t-privw", tags: [
    "Token Impersonation","SeImpersonatePrivilege","SeDebugPrivilege",
    "SeBackupPrivilege","SeRestorePrivilege","AlwaysInstallElevated",
    "Writable Service","Unquoted Service Path","DLL Hijacking","DLL Side-Loading",
    "ACL Abuse","Registry Abuse","Scheduled Task Abuse","UAC Bypass",
    "PrintSpoofer","JuicyPotato","RoguePotato","GodPotato","SweetPotato",
    "AMSI Bypass","SAM Dump","LSASS Dump","Credential Manager","Startup Abuse","Process Injection",
  ]},
  { label: "AD", cls: "t-ad", tags: [
    "Kerberoasting","AS-REP Roasting","Pass-the-Hash","Pass-the-Ticket",
    "Overpass-the-Hash","DCSync","Golden Ticket","Silver Ticket","Skeleton Key",
    "BloodHound","ACL Abuse","GPO Abuse","DACL Abuse","ForceChangePassword",
    "GenericWrite","WriteDACL","Constrained Delegation","Unconstrained Delegation",
    "Resource-Based Delegation","PrintNightmare","ZeroLogon","PetitPotam","NoPac",
    "NTLM Relay","SMB Relay","LLMNR Poisoning","NBT-NS Poisoning","mitm6",
    "Cross-Domain Trust","Child-to-Parent",
  ]},
  { label: "Network", cls: "t-net", tags: [
    "FTP Anonymous","SMB Null Session","RPC Enum","SNMP Enum","NFS Exploit",
    "SMTP User Enum","DNS Zone Transfer","ARP Spoofing","DNS Poisoning",
    "Port Knocking","Redis Exploit","MongoDB NoAuth","Elasticsearch",
    "Jenkins RCE","Tomcat RCE","WebLogic RCE","JBoss RCE","Drupal RCE",
    "WordPress Plugin RCE","GitLab RCE","VNC Exploit","RDP Exploit","Telnet Abuse",
  ]},
  { label: "Crypto", cls: "t-crypto", tags: [
    "Hash Cracking","Rainbow Table","Padding Oracle","CBC Bit Flipping",
    "ECB Oracle","RSA Low Exponent","RSA Common Modulus","RSA Factoring",
    "Weak PRNG","XOR Cipher","Vigenere","Caesar / ROT","Base32/64/85",
    "JWT RS256→HS256","Length Extension","ECDSA Nonce Reuse","Weak IV",
  ]},
  { label: "Forensics", cls: "t-stego", tags: [
    "LSB Steganography","Audio Stego","EXIF Metadata","Binwalk","File Carving",
    "Disk Forensics","Memory Forensics","Volatility","PCAP Analysis",
    "Log Analysis","Hex Analysis","Strings Analysis","Zip Password Crack","PDF Metadata",
  ]},
  { label: "BinExp", cls: "t-bin", tags: [
    "Buffer Overflow","Stack Overflow","Heap Overflow","Use-After-Free",
    "Format String","ROP Chain","ret2libc","ret2plt","ret2win",
    "ASLR Bypass","PIE Bypass","NX Bypass","Canary Bypass","GOT Overwrite",
    "Shellcode","Tcache Poisoning","Fast Bin Attack","Integer Overflow","Off-by-One","Type Confusion",
  ]},
  { label: "RevEng", cls: "t-re", tags: [
    "Static Analysis","Dynamic Analysis","Ghidra","IDA Pro","x64dbg",
    "GDB / pwndbg","Radare2","Anti-Debug Bypass","Anti-VM Bypass",
    "Unpacking","Deobfuscation","Firmware Analysis",".NET Reversing",
    "Java Reversing","APK Reversing","Patching Binary","Keygen",
  ]},
  { label: "Cloud", cls: "t-cloud", tags: [
    "AWS SSRF → IMDSv1","S3 Misconfiguration","IAM PrivEsc","Lambda Abuse",
    "Azure AD Enum","GCP Metadata","Docker Escape","K8s RBAC Abuse",
    "K8s API Server","Exposed Secrets in ENV","CI/CD Abuse","GitHub Actions Abuse",
  ]},
  { label: "Misc", cls: "t-misc", tags: [
    "Default Credentials","Credential Reuse","Hardcoded Secrets","Git Exposure",
    "Backup File Disclosure","Source Code Disclosure","Null Byte Injection",
    "Unicode Bypass","Weak Regex","Password in Logs","Env Variable Leak",
    "Certificate Transparency","WHOIS Recon",
  ]},
];

const TECHNIQUES_DATA = [
  { label: "Recon", cls: "t-recon", tags: [
    "Port Scanning","Service Fingerprinting","OS Detection","Directory Bruting",
    "File Extension Bruting","Virtual Host Discovery","Subdomain Enumeration",
    "DNS Enumeration","DNS Zone Transfer","SMB Enumeration","SNMP Walk",
    "LDAP Enumeration","RPC Enumeration","User Enumeration","OSINT",
    "Google Dorking","GitHub Dorking","Shodan","Wayback Machine","Fuzzing",
    "Parameter Discovery","API Endpoint Discovery","JS File Analysis",
    "Source Map Extraction","Robots.txt","Certificate Transparency","Email Harvesting",
  ]},
  { label: "Exploit", cls: "t-exp", tags: [
    "Exploit Chaining","CVE Exploitation","Public PoC Abuse","Metasploit",
    "Manual Exploitation","Brute Force","Password Spraying","Credential Stuffing",
    "Wordlist Attack","Mask Attack","Rule-Based Attack","Phishing",
    "Client-Side Attack","Malicious Macro","WAF Bypass","Filter Evasion",
    "Double Encoding","Null Byte Bypass","Case Manipulation",
  ]},
  { label: "Post-Exploit", cls: "t-post", tags: [
    "Reverse Shell","Bind Shell","Web Shell","Meterpreter","Persistence",
    "Credential Dumping","SAM / NTDS Dump","LSASS Dump","Mimikatz","Keylogging",
    "Data Exfiltration","Exfil via DNS","Exfil via ICMP","Log Clearing",
    "DLL Injection","Reflective DLL","Scheduled Task Backdoor","Service Backdoor",
    "Registry Backdoor","SSH Key Persistence","Crontab Backdoor",
    "WMI Subscription","Browser Password Dump",
  ]},
  { label: "Lateral", cls: "t-lat", tags: [
    "Pass-the-Hash","Pass-the-Ticket","Overpass-the-Hash","Golden Ticket",
    "Silver Ticket","WMI Execution","PSExec","SMBExec","WinRM Abuse",
    "RDP Hijacking","SSH Agent Forwarding","DCOM Execution","BloodHound Path",
    "Kerberos Delegation","Token Impersonation","Credential Reuse",
    "AT Job Execution","SCM Abuse",
  ]},
  { label: "Evasion", cls: "t-evas", tags: [
    "AMSI Bypass","UAC Bypass","ETW Patching","AV Evasion","EDR Bypass",
    "Obfuscation","Payload Encoding","LOLBAS","Living off the Land",
    "Signed Binary Proxy","Parent PID Spoofing","Timestomping",
    "Alternate Data Streams","AppLocker Bypass","CLM Bypass",
    "Firewall Evasion","IDS/IPS Evasion",
  ]},
  { label: "Tunneling", cls: "t-tunnel", tags: [
    "SSH Tunneling","SSH Dynamic Forward","SSH Reverse Tunnel","Port Forwarding",
    "Chisel","Ligolo-ng","Proxychains","SOCKS5 Proxy","HTTP Tunnel",
    "DNS Tunneling","ICMP Tunneling","Double Pivot","Metasploit Route",
    "Rpivot","VPN Pivoting",
  ]},
];

/* ─────────────────────────────────────────
   FIX: MemoImage — stable component, never
   remounts when parent re-renders on scroll
───────────────────────────────────────── */
const MemoImage = memo(({ src, alt, apiBase }) => {
  const final = src?.startsWith("http") ? src : `${apiBase}${src}`;
  return (
    <Zoom>
      <img src={final} alt={alt ?? ""} className="ctf-img" loading="lazy" />
    </Zoom>
  );
});

/* ─────────────────────────────────────────
   FIX: ScrollBar — isolated component so
   scroll state never touches the article
───────────────────────────────────────── */
const ScrollBar = () => {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let ticking = false;
    const fn = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const d     = document.documentElement;
        const total = d.scrollHeight - d.clientHeight;
        setPct(total > 0 ? Math.min(100, (d.scrollTop / total) * 100) : 0);
        ticking = false;
      });
    };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return <div className="ctf-progress" style={{ width:`${pct}%` }} />;
};

/* ─────────────────────────────────────────
   INJECT FONTS + CSS
───────────────────────────────────────── */
function useFonts() {
  useEffect(() => {
    if (document.querySelector("link[data-ctf-fonts]")) return;
    const l = document.createElement("link");
    l.rel  = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Syne:wght@700;800&family=JetBrains+Mono:ital,wght@0,300;0,400;0,500;0,700;1,400&display=swap";
    l.setAttribute("data-ctf-fonts", "1");
    document.head.appendChild(l);
  }, []);
}
function useStyles() {
  useEffect(() => {
    if (document.querySelector("style[data-ctf]")) return;
    const el = document.createElement("style");
    el.setAttribute("data-ctf", "1");
    el.textContent = CTF_CSS;
    document.head.appendChild(el);
  }, []);
}

/* ─────────────────────────────────────────
   ATOMS
───────────────────────────────────────── */
const Cursor   = () => <span className="ctf-cursor" />;
const Scanline = () => <span className="ctf-scanline" aria-hidden="true" />;

const Badge = ({ label, color, rgb }) => (
  <span className="ctf-badge"
    style={{ color, border:`1px solid rgba(${rgb},.4)`, background:`rgba(${rgb},.08)` }}>
    {label}
  </span>
);

const FlowTag = ({ label }) => {
  const key = (label || "").toLowerCase().replace(/[^a-z]/g, "");
  const c   = TAG_COLORS[key] || TAG_COLORS.default;
  return (
    <span className="ctf-flow-tag"
      style={{ background:c.bg, border:`1px solid ${c.border}`, color:c.color }}>
      {label}
    </span>
  );
};

/* ─────────────────────────────────────────
   PWNED BANNER
───────────────────────────────────────── */
const PwnedBanner = memo(({ writeup, machineName }) => {
  const vulns  = writeup.vuln_count  || null;
  const stages = writeup.stage_count || null;
  const cves   = writeup.cve_count   || null;
  return (
    <div className="ctf-pwned-banner">
      <Scanline />
      <div className="ctf-pwned-skull">💀</div>
      <div className="ctf-pwned-text">
        <div className="ctf-pwned-owned">{machineName} HAS BEEN PWNED</div>
        <div className="ctf-pwned-sub">
          root@{machineName.toLowerCase()}:~# id &nbsp;→&nbsp;
          uid=0(root) gid=0(root) groups=0(root)
        </div>
      </div>
      {(vulns || stages || cves) && (
        <div className="ctf-pwned-stats">
          {vulns  && <div className="ctf-stat"><span className="ctf-stat-n">{vulns}</span><span className="ctf-stat-l">Vulns</span></div>}
          {stages && <div className="ctf-stat"><span className="ctf-stat-n">{stages}</span><span className="ctf-stat-l">Stages</span></div>}
          {cves   && <div className="ctf-stat"><span className="ctf-stat-n">CVE</span><span className="ctf-stat-l">Custom Chain</span></div>}
        </div>
      )}
    </div>
  );
});

/* ─────────────────────────────────────────
   SKILLS PANEL
───────────────────────────────────────── */
const SkillsPanel = memo(({ writeup }) => {
  const [activeTab, setActiveTab] = useState("skills");
  const [query,     setQuery]     = useState("");

  const savedSkills     = Array.isArray(writeup?.skills)     ? writeup.skills     : [];
  const savedTechniques = Array.isArray(writeup?.techniques) ? writeup.techniques : [];

  const filterCats = (allData, saved) =>
    allData
      .map(cat => ({
        ...cat,
        tags: saved.length ? cat.tags.filter(t => saved.includes(t)) : cat.tags,
      }))
      .filter(cat => cat.tags.length > 0);

  const skillsCats = filterCats(SKILLS_DATA,     savedSkills);
  const techCats   = filterCats(TECHNIQUES_DATA, savedTechniques);

  const sourceData = activeTab === "skills" ? skillsCats : techCats;
  const q = query.toLowerCase().trim();
  const display = q
    ? sourceData.map(c => ({ ...c, tags: c.tags.filter(t => t.toLowerCase().includes(q)) }))
        .filter(c => c.tags.length > 0)
    : sourceData;

  const totalSkills = skillsCats.reduce((n, c) => n + c.tags.length, 0);
  const totalTechs  = techCats.reduce((n, c) => n + c.tags.length, 0);

  if (totalSkills === 0 && totalTechs === 0) return null;

  return (
    <div className="sp-wrap">
      <div className="sp-hdr">
        <span className="sp-num">03</span>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{flexShrink:0}}>
          <circle cx="8" cy="8" r="7" stroke="#1D9E75" strokeWidth="1.4"/>
          <path d="M5 8l2 2 4-4" stroke="#1D9E75" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="sp-title">Skills &amp; Techniques</span>
        <span className="sp-total">{activeTab === "skills" ? totalSkills : totalTechs} tags</span>
        <div className="sp-rule" />
      </div>
      <div className="sp-search-wrap">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
          style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>
          <circle cx="6.5" cy="6.5" r="5" stroke="#2e4a5a" strokeWidth="1.5"/>
          <path d="M10 10l3.5 3.5" stroke="#2e4a5a" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input className="sp-search" placeholder="filter tags…" value={query} onChange={e => setQuery(e.target.value)} />
      </div>
      <div className="sp-tabs">
        <button className={`sp-tab${activeTab==="skills"?" active":""}`}
          onClick={() => { setActiveTab("skills"); setQuery(""); }}>
          // Skills {totalSkills > 0 && <span className="sp-tab-n">{totalSkills}</span>}
        </button>
        <button className={`sp-tab sp-tab-cyan${activeTab==="techniques"?" active":""}`}
          onClick={() => { setActiveTab("techniques"); setQuery(""); }}>
          // Techniques {totalTechs > 0 && <span className="sp-tab-n sp-tab-n-cyan">{totalTechs}</span>}
        </button>
      </div>
      <div className="sp-body">
        {display.length === 0 ? (
          <div className="sp-empty">no tags match "{query}"</div>
        ) : (
          display.map(cat => (
            <div key={cat.label} className="sp-cat">
              <div className="sp-cat-label">{cat.label}</div>
              <div className="sp-tags">
                {cat.tags.map(tag => (
                  <span key={tag} className={`sp-tag ${cat.cls}`}>{tag}</span>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
});

/* ─────────────────────────────────────────
   ATTACK CHAIN
───────────────────────────────────────── */
const AttackChain = memo(({ writeup }) => {
  let chain = writeup.attack_chain;
  if (typeof chain === "string") {
    try { chain = JSON.parse(chain); } catch { chain = []; }
  }
  if (!Array.isArray(chain) || !chain.length) return null;
  return (
    <div className="ctf-chain-section">
      <div className="ctf-sec-hdr">
        <span className="ctf-sec-num">00</span>
        <h2 className="ctf-sec-title">Attack Chain Overview</h2>
        <div className="ctf-sec-rule" />
      </div>
      <div className="ctf-attack-flow">
        {chain.map((s, i) => (
          <div key={i} className="ctf-flow-step">
            <div className="ctf-flow-num">{String(i + 1).padStart(2, "0")}</div>
            <div className="ctf-flow-body">
              <div className="ctf-flow-title-row">
                <span className="ctf-flow-title">{s.title}</span>
                {s.tag && <FlowTag label={s.tag} />}
              </div>
              {s.description && (
                <p className="ctf-flow-desc"
                  dangerouslySetInnerHTML={{ __html: s.description.replace(/`([^`]+)`/g, '<code>$1</code>') }} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

/* ─────────────────────────────────────────
   ARTICLE — memoized so scroll never
   causes it to re-render at all
───────────────────────────────────────── */
const Article = memo(({ content, apiBase }) => {
  /* FIX: components object built once per unique content+apiBase,
     never recreated on scroll re-renders */
  const components = useMemo(() => ({
    img: ({ src, alt }) => <MemoImage src={src} alt={alt} apiBase={apiBase} />,

    code({ node, inline, className, children }) {
      const match = /language-(\w+)/.exec(className || "");
      const lang  = match ? match[1] : "";
      const body  = String(children).replace(/\n$/, "");
      if (inline) return <code className="ctf-inline-code">{children}</code>;
      if (lang === "terminal") return <TerminalBlock>{body}</TerminalBlock>;
      if (lang === "output")   return <OutputBlock>{body}</OutputBlock>;
      return <CodeBlock language={lang}>{body}</CodeBlock>;
    },

    h2({ node, children, ...props }) {
      const plain = childrenToText(children);
      return (
        <h2 {...props} data-plain={plain} className="ctf-h2">
          <a href={props.id ? `#${props.id}` : undefined}
            className="ctf-h2-anchor" aria-hidden="true" tabIndex={-1}>
            <span className="ctf-h2-hash">##&nbsp;</span>
          </a>
          {children}
        </h2>
      );
    },

    h3({ node, children, ...props }) {
      return <h3 {...props} className="ctf-h3"><span className="ctf-h3-hash">###&nbsp;</span>{children}</h3>;
    },

    blockquote: ({ children }) => (
      <blockquote className="ctf-blockquote">
        <span className="ctf-bq-bar" aria-hidden="true" />
        <div className="ctf-bq-body">{children}</div>
      </blockquote>
    ),

    table:  ({ children }) => <div className="ctf-table-wrap"><table className="ctf-table">{children}</table></div>,
    th: ({ children }) => <th className="ctf-th">{children}</th>,
    td: ({ children }) => <td className="ctf-td">{children}</td>,
    tr: ({ children }) => <tr className="ctf-tr">{children}</tr>,

    a: ({ href, children }) => (
      <a href={href} className="ctf-a"
        target={href?.startsWith("http") ? "_blank" : undefined}
        rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}>
        {children}
      </a>
    ),

    hr: () => (
      <div className="ctf-divider" aria-hidden="true">
        <span /><span className="ctf-divider-gem">◈</span><span />
      </div>
    ),

    p:  ({ children }) => <p  className="ctf-p">{children}</p>,
    ul: ({ children }) => <ul className="ctf-ul">{children}</ul>,
    ol: ({ children }) => <ol className="ctf-ol">{children}</ol>,
    li: ({ children }) => (
      <li className="ctf-li">
        <span className="ctf-li-bullet" aria-hidden="true">▸</span>
        <span>{children}</span>
      </li>
    ),
  }), [apiBase]);

  return (
    <div className="ctf-prose prose-writeup">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight, rehypeSlug]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});

/* ═══════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════ */
const WriteupPage = () => {
  const { id }   = useParams();
  const navigate = useNavigate();

  useFonts();
  useStyles();

  const [writeup,       setWriteup]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [headings,      setHeadings]      = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [copied,        setCopied]        = useState(false);
  const [siblings,      setSiblings]      = useState({ prev: null, next: null });
  const [related,       setRelated]       = useState([]);
  const articleRef  = useRef(null);
  const headingsSet = useRef(false);

  const fetchWriteup = useCallback(async () => {
    try {
      // URLs are slugs (e.g. /writeup/pg-kevin). Old /writeup/<uuid> links still
      // resolve directly. A slug is matched against the list, then loaded by id
      // (so the view counter still increments on the real record).
      let realId = id;
      if (!isUuid(id)) {
        const r = await writeupAPI.getAll();
        const list = Array.isArray(r.data) ? r.data : (r.data?.writeups || r.data?.items || []);
        const match = list.find((w) => slugify(w.title) === id);
        if (!match) { navigate("/writeups"); return; }
        realId = match.id;
      }
      const r = await writeupAPI.getOne(realId);
      setWriteup(r.data);
    } catch (err) {
      console.error("WriteupPage fetch error:", err);
      navigate("/writeups");
    }
  }, [id, navigate]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      headingsSet.current = false;
      await fetchWriteup();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [fetchWriteup]);

  // Canonicalise: an old UUID URL quietly upgrades to the readable slug URL.
  useEffect(() => {
    if (writeup && isUuid(id)) {
      navigate(writeupPath(writeup), { replace: true });
    }
  }, [writeup, id, navigate]);

  useEffect(() => {
    if (!writeup || headingsSet.current) return;
    const t = setTimeout(() => {
      const els = Array.from(document.querySelectorAll(".ctf-prose h2[id]"));
      if (els.length > 0) {
        setHeadings(els.map(el => ({ id: el.id, text: el.dataset.plain || el.textContent || "" })));
        headingsSet.current = true;
      }
    }, 300);
    return () => clearTimeout(t);
  }, [writeup]);

  useEffect(() => {
    const go = () => {
      const hash = window.location.hash;
      if (!hash || hash === "#") return;
      try {
        const el = document.querySelector(hash);
        if (el) setTimeout(() => el.scrollIntoView({ behavior:"smooth", block:"start" }), 80);
      } catch (e) { console.warn("hash scroll:", e); }
    };
    go();
    window.addEventListener("hashchange", go);
    return () => window.removeEventListener("hashchange", go);
  }, [writeup]);

  useEffect(() => {
    if (!headings.length) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }),
      { rootMargin: "-35% 0px -60% 0px" }
    );
    document.querySelectorAll(".ctf-prose h2[id]").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [headings]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  }, []);

  // Find the previous / next writeup (by publish date) for end-of-article nav.
  useEffect(() => {
    if (!writeup) return;
    let alive = true;
    (async () => {
      try {
        const r = await writeupAPI.getAll();
        const list = Array.isArray(r.data) ? r.data : (r.data?.writeups || r.data?.items || []);
        const sorted = [...list].sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
        const idx = sorted.findIndex((w) => w.id === writeup.id);
        if (alive && idx !== -1) {
          setSiblings({ prev: sorted[idx - 1] || null, next: sorted[idx + 1] || null });
        }

        // Related-by-technique: someone who finished an AD box wants another AD
        // box, not whatever was published next. Score overlap of tags, skills,
        // platform and OS; fall back to recency only to fill the row.
        if (alive) {
          const lc = (a) => (Array.isArray(a) ? a.map((x) => String(x).toLowerCase()) : []);
          const myTags   = new Set(lc(writeup.tags));
          const mySkills = new Set(lc(writeup.skills));
          const scored = list
            .filter((w) => w.id !== writeup.id && w.published !== false)
            .map((w) => {
              let s = 0;
              lc(w.tags).forEach((t) => { if (myTags.has(t)) s += 2; });
              lc(w.skills).forEach((t) => { if (mySkills.has(t)) s += 2; });
              if (w.platform === writeup.platform) s += 1;
              if (w.os_type === writeup.os_type) s += 1;
              return { w, s };
            })
            .sort((a, b) =>
              b.s - a.s || (new Date(b.w.created_at || 0) - new Date(a.w.created_at || 0))
            );
          setRelated(scored.slice(0, 3).map((x) => x.w));
        }
      } catch (e) {
        console.warn("siblings fetch:", e);
      }
    })();
    return () => { alive = false; };
  }, [writeup]);

  if (loading) {
    return (
      <div className="ctf-loading">
        <span className="ctf-loading-text">Loading writeup<Cursor /></span>
      </div>
    );
  }
  if (!writeup) return null;

  const diff        = DIFF[writeup.difficulty?.toLowerCase()] ?? DIFF.medium;
  const mins        = readingTime(writeup.content || "");
  const API         = process.env.REACT_APP_BACKEND_URL || "https://maxwell-backend-fyzj.onrender.com";
  const machineName = (writeup.machine_name || writeup.title || "machine").toUpperCase();
  const tagList     = Array.isArray(writeup.tags) ? writeup.tags.slice(0, 5) : [];
  const intelItems  = [
    { label: "Target", value: writeup.machine_name || writeup.title || "Unknown" },
    { label: "Platform", value: formatPlatform(writeup.platform) },
    { label: "Difficulty", value: diff.label },
    { label: "OS", value: formatOsLabel(writeup.os_type) || "Mixed" },
    { label: "Skills", value: Array.isArray(writeup.skills) && writeup.skills.length ? `${writeup.skills.length} tracked` : "General" },
    { label: "CVEs", value: Array.isArray(writeup.cves) && writeup.cves.length ? writeup.cves[0] : "Custom chain" }
  ];

  const canonicalPath = writeupPath(writeup);
  const canonicalUrl  = `${SITE_ORIGIN}${canonicalPath}`;
  const keywords = [
    ...(Array.isArray(writeup.tags) ? writeup.tags : []),
    ...(Array.isArray(writeup.skills) ? writeup.skills : []),
  ].join(", ");
  const writeupJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: writeup.title,
      description: writeup.description || undefined,
      image: writeup.cover_image || undefined,
      datePublished: writeup.created_at || undefined,
      dateModified: writeup.updated_at || writeup.created_at || undefined,
      author: { "@type": "Person", name: "Maxwell Ferreira", url: `${SITE_ORIGIN}/about` },
      publisher: { "@type": "Person", name: "Maxwell Ferreira" },
      keywords: keywords || undefined,
      mainEntityOfPage: canonicalUrl,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Writeups", item: `${SITE_ORIGIN}/writeups` },
        { "@type": "ListItem", position: 2, name: writeup.title, item: canonicalUrl },
      ],
    },
  ];

  return (
    <>
      {/* FIX: ScrollBar is its own component — its state updates never
          touch WriteupPage or Article, so images never re-render */}
      <ScrollBar />

      <SEO
        title={writeup.title}
        description={writeup.description}
        image={writeup.cover_image}
        type="article"
        canonical={canonicalPath}
        jsonLd={writeupJsonLd}
      />

      {/* HERO */}
      <header className="ctf-hero">
        <div className="ctf-hero-grid"  aria-hidden="true" />
        <div className="ctf-hero-glow"  aria-hidden="true" />
        <div className="ctf-hero-noise" aria-hidden="true" />
        <div className="ctf-hero-inner">
          <Link to="/writeups" className="ctf-back"><ArrowLeft size={13} /> Back to Writeups</Link>
          <div className="ctf-hero-shell">
            <div className="ctf-hero-main">
              <div className="ctf-badges">
            {writeup.platform   && <Badge label={formatPlatform(writeup.platform)} color="#00ff88" rgb="0,255,136" />}
            {writeup.difficulty && <Badge label={diff.label}        color={diff.color} rgb={diff.rgb} />}
            {writeup.os_type    && <Badge label={formatOsLabel(writeup.os_type)} color="#00d4ff" rgb="0,212,255" />}
            <Badge label="★ Pwned" color="#ff4455" rgb="255,68,85" />
          </div>
          <h1 className="ctf-title">{writeup.title}</h1>
          <div className="ctf-meta">
            <span><User     size={12} /> {writeup.author || "Maxwell Ferreira"}</span>
            <span><Calendar size={12} /> {formatDate(writeup.created_at)}</span>
            <span><Clock    size={12} /> {mins} min read</span>
          </div>
          {writeup.description && <p className="ctf-synopsis">{writeup.description}</p>}
          {tagList.length > 0 && (
            <div className="ctf-tag-row" aria-label="Writeup tags">
              {tagList.map((tag) => (
                <span key={tag} className="ctf-tag-chip">
                  #{tag}
                </span>
              ))}
            </div>
          )}
          <div className="ctf-share-bar">
            <button onClick={handleCopy} className="ctf-action-btn">
              {copied ? <><Check size={12}/><span>Copied!</span></> : <><Copy size={12}/><span>Copy link</span></>}
            </button>
            <a className="ctf-action-btn"
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(writeup.title)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank" rel="noopener noreferrer">
              <Share2 size={12}/><span>Share</span>
            </a>
          </div>
            </div>

            <aside className="ctf-hero-rail" aria-label="Writeup quick intel">
              <div className="ctf-intel-card">
                <div className="ctf-intel-head">
                  <span className="ctf-intel-kicker">Quick Intel</span>
                  <span className="ctf-intel-status">Live Notes</span>
                </div>
                <div className="ctf-intel-grid">
                  {intelItems.map((item) => (
                    <div key={item.label} className="ctf-intel-item">
                      <span className="ctf-intel-k">{item.label}</span>
                      <span className="ctf-intel-v">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </header>

      {/* SUB-HERO */}
      <div className="ctf-subhero">
        {writeup.cover_image && (
          <div className="ctf-cover-wrap">
            <Zoom><img src={writeup.cover_image} alt={writeup.title} className="ctf-cover" /></Zoom>
          </div>
        )}
        <PwnedBanner writeup={writeup} machineName={machineName} />
        <SkillsPanel writeup={writeup} />
        <AttackChain writeup={writeup} />
      </div>

      {/* ARTICLE */}
      <div className="ctf-page">
        <div className="ctf-layout">
          <aside className="ctf-toc" aria-label="Table of contents">
            <div className="ctf-toc-header"><span className="ctf-toc-prefix">// </span>CONTENTS</div>
            <nav>
              <ul className="ctf-toc-list">
                {headings.map((h, i) => (
                  <li key={h.id}>
                    <a href={`#${h.id}`} className={`ctf-toc-item${activeSection === h.id ? " is-active" : ""}${/lessons?\s*learned/i.test(h.text) ? " is-lessons" : ""}`}>
                      <span className="ctf-toc-n">{String(i+1).padStart(2,"00")}</span>
                      <ChevronRight size={10} />
                      <span className="ctf-toc-label">{h.text}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="ctf-toc-deco" aria-hidden="true"><div /><div /><div /><div /></div>
          </aside>

          <article className="ctf-article" ref={articleRef}>
            <Article content={writeup.content} apiBase={API} />

            {/* AUTHOR CTA — the end of a writeup someone read in full is the
                highest-intent moment on the site. Give them somewhere to go. */}
            <aside className="ctf-author" aria-label="About the author">
              {SITE.photoUrl ? (
                <img src={SITE.photoUrl} alt={SITE.name} className="ctf-author-photo" width="72" height="72" loading="lazy" />
              ) : (
                <div className="ctf-author-photo ctf-author-glyph" aria-hidden="true">&gt;_</div>
              )}
              <div className="ctf-author-body">
                <div className="ctf-author-name">{SITE.name}</div>
                <p className="ctf-author-line">
                  Penetration tester in Sydney. {CREDENTIALS.slice(0, 3).join(" · ")}.
                  I document every machine command by command — recon to root.
                </p>
                <div className="ctf-author-actions">
                  <Link to="/contact" className="ctf-author-btn ctf-author-btn-primary">
                    <Mail size={13} /> Get in touch
                  </Link>
                  <a href={SITE.resumeUrl} download className="ctf-author-btn">
                    <FileText size={13} /> Résumé
                  </a>
                  {SITE.social?.hackTheBox && (
                    <a href={SITE.social.hackTheBox} target="_blank" rel="noopener noreferrer" className="ctf-author-btn">
                      HTB profile <ArrowUpRight size={12} />
                    </a>
                  )}
                </div>
              </div>
            </aside>
          </article>
        </div>

        {related.length > 0 && (
          <section className="ctf-related" aria-label="Related writeups">
            <div className="ctf-related-head">
              <span className="ctf-related-kicker">// keep going</span>
              <h2 className="ctf-related-title">Related writeups</h2>
            </div>
            <div className="ctf-related-grid">
              {related.map((w) => {
                const d = DIFF[w.difficulty?.toLowerCase()] ?? DIFF.medium;
                return (
                  <Link key={w.id} to={writeupPath(w)} className="ctf-related-card">
                    <div className="ctf-related-badges">
                      <span className="ctf-related-chip" style={{ color: d.color, borderColor: `rgba(${d.rgb},.35)` }}>{d.label}</span>
                      <span className="ctf-related-chip">{formatPlatform(w.platform)}</span>
                      {w.os_type && <span className="ctf-related-chip">{formatOsLabel(w.os_type)}</span>}
                    </div>
                    <h3 className="ctf-related-name">{w.title}</h3>
                    {w.description && <p className="ctf-related-desc">{w.description}</p>}
                    <span className="ctf-related-open">Open writeup <ArrowUpRight size={12} /></span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {(siblings.prev || siblings.next) && (
          <nav className="ctf-pager" aria-label="More writeups">
            {siblings.prev ? (
              <Link to={writeupPath(siblings.prev)} className="ctf-pager-card ctf-pager-prev">
                <span className="ctf-pager-dir"><ArrowLeft size={12} /> Previous</span>
                <span className="ctf-pager-title">{siblings.prev.title}</span>
              </Link>
            ) : <span className="ctf-pager-spacer" />}
            {siblings.next ? (
              <Link to={writeupPath(siblings.next)} className="ctf-pager-card ctf-pager-next">
                <span className="ctf-pager-dir">Next <ArrowRight size={12} /></span>
                <span className="ctf-pager-title">{siblings.next.title}</span>
              </Link>
            ) : <span className="ctf-pager-spacer" />}
          </nav>
        )}
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════════ */
const CTF_CSS = `
@keyframes ctf-blink    { 0%,100%{opacity:1} 50%{opacity:0} }
@keyframes ctf-scanline { 0%{transform:translateX(-100%)} 100%{transform:translateX(300%)} }
@keyframes ctf-fadein   { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
@keyframes ctf-shimmer  { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
@keyframes ctf-pulse    { 0%,100%{box-shadow:0 0 0 0 rgba(0,255,136,0)} 50%{box-shadow:0 0 0 6px rgba(0,255,136,.07)} }
@keyframes ctf-glow     { 0%,100%{opacity:.6} 50%{opacity:1} }

::-webkit-scrollbar{width:5px;height:5px}
::-webkit-scrollbar-track{background:#070a0e}
::-webkit-scrollbar-thumb{background:#1e3048;border-radius:3px}
::-webkit-scrollbar-thumb:hover{background:#2b4a6a}

.ctf-progress{position:fixed;top:0;left:0;z-index:9999;height:2px;background:linear-gradient(90deg,#00ff88,#00d4ff,#00ff88);background-size:200% 100%;animation:ctf-shimmer 2.2s linear infinite;box-shadow:0 0 10px rgba(0,255,136,.5),0 0 22px rgba(0,212,255,.25);transition:width .12s linear;pointer-events:none;will-change:width}
.ctf-cursor{display:inline-block;width:8px;height:15px;background:#00ff88;vertical-align:middle;margin-left:3px;animation:ctf-blink 1.1s step-end infinite}
.ctf-badge{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:11px;letter-spacing:.1em;text-transform:uppercase;padding:4px 12px;border-radius:2px}
.ctf-scanline{position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,#00ff88,transparent);animation:ctf-scanline 3.5s linear infinite;pointer-events:none}
.ctf-loading{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#070a0e}
.ctf-loading-text{font-family:'Share Tech Mono',ui-monospace,monospace;color:#00ff88;font-size:14px;display:flex;align-items:center;gap:4px}

.ctf-hero{position:relative;overflow:hidden;background:#070a0e;padding:4.5rem 0 0;border-bottom:1px solid #1a2738}
.ctf-hero-grid{position:absolute;inset:0;pointer-events:none;background-image:linear-gradient(rgba(0,255,136,.028) 1px,transparent 1px),linear-gradient(90deg,rgba(0,255,136,.028) 1px,transparent 1px);background-size:44px 44px;-webkit-mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.85) 15%,rgba(0,0,0,.85) 85%,transparent);mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.85) 15%,rgba(0,0,0,.85) 85%,transparent)}
.ctf-hero-glow{position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse 820px 360px at 50% -30%,rgba(0,255,136,.07) 0%,transparent 70%),radial-gradient(ellipse 440px 280px at 92% 55%,rgba(0,212,255,.05) 0%,transparent 65%)}
.ctf-hero-noise{position:absolute;inset:0;pointer-events:none;opacity:.016;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E")}
.ctf-hero-inner{position:relative;z-index:1;max-width:900px;margin:0 auto;padding:0 2rem 3rem;animation:ctf-fadein .55s ease both}
.ctf-hero-shell{display:grid;grid-template-columns:minmax(0,1fr) 280px;gap:1.4rem;align-items:start}
.ctf-hero-main{min-width:0}
.ctf-hero-rail{position:relative}
.ctf-intel-card{padding:1rem;border:1px solid rgba(0,212,255,.18);border-radius:3px;background:linear-gradient(180deg,rgba(12,17,24,.96) 0%,rgba(7,10,14,.98) 100%);box-shadow:0 20px 60px rgba(0,0,0,.24)}
.ctf-intel-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:.9rem}
.ctf-intel-kicker{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:#00d4ff}
.ctf-intel-status{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#56657a}
.ctf-intel-grid{display:grid;grid-template-columns:1fr;gap:8px}
.ctf-intel-item{padding:.8rem .85rem;border:1px solid #1a2738;border-radius:3px;background:rgba(255,255,255,.02)}
.ctf-intel-k{display:block;font-family:'Share Tech Mono',ui-monospace,monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#56657a}
.ctf-intel-v{display:block;margin-top:6px;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:12px;line-height:1.6;color:#e2eaf3}
.ctf-back{display:inline-flex;align-items:center;gap:7px;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:12px;letter-spacing:.05em;color:#56657a;text-decoration:none;margin-bottom:1.8rem;transition:color .2s}
.ctf-back:hover{color:#00ff88}
.ctf-badges{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:1.3rem}
.ctf-title{font-family:'Syne',system-ui,sans-serif;font-size:clamp(2rem,5.5vw,3.3rem);font-weight:800;letter-spacing:-.025em;color:#e2eaf3;line-height:1.07;margin:0 0 .9rem}
.ctf-meta{display:flex;flex-wrap:wrap;gap:1.4rem;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:12px;color:#56657a;margin-bottom:1.5rem}
.ctf-meta span{display:flex;align-items:center;gap:6px}
.ctf-synopsis{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:13.5px;color:#b8c4d0;line-height:1.82;padding:1.3rem 1.6rem;border-left:2px solid #00ff88;background:rgba(0,255,136,.04);border-radius:0 3px 3px 0;max-width:700px;margin-bottom:1.7rem}
.ctf-tag-row{display:flex;flex-wrap:wrap;gap:8px;margin:-.4rem 0 1.4rem}
.ctf-tag-chip{display:inline-flex;align-items:center;padding:4px 10px;border:1px solid #1a2738;border-radius:999px;background:rgba(255,255,255,.02);font-family:'Share Tech Mono',ui-monospace,monospace;font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#8ea0b4}
.ctf-share-bar{display:flex;gap:8px;flex-wrap:wrap}
.ctf-action-btn{display:inline-flex;align-items:center;gap:5px;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:11px;letter-spacing:.04em;color:#56657a;background:rgba(255,255,255,.025);border:1px solid #1a2738;border-radius:3px;padding:5px 11px;cursor:pointer;text-decoration:none;transition:all .18s;-webkit-appearance:none;appearance:none}
.ctf-action-btn:hover{border-color:rgba(0,255,136,.4);color:#00ff88;background:rgba(0,255,136,.05)}

.ctf-subhero{background:#070a0e;background-image:linear-gradient(rgba(0,255,136,.012) 1px,transparent 1px),linear-gradient(90deg,rgba(0,255,136,.012) 1px,transparent 1px);background-size:44px 44px;padding:0 0 1.5rem}
.ctf-cover-wrap{max-width:900px;margin:0 auto;padding:2.5rem 2rem 0}
.ctf-cover{width:100%;display:block;border-radius:3px;border:1px solid #1a2738}

.ctf-pwned-banner{position:relative;overflow:hidden;max-width:900px;margin:2rem auto 0;padding:1.4rem 2rem;display:flex;align-items:center;gap:1.6rem;flex-wrap:wrap;background:linear-gradient(135deg,#0c1118 0%,#081309 100%);border:1px solid rgba(0,255,136,.22);border-radius:3px;animation:ctf-pulse 4.5s ease infinite}
.ctf-pwned-skull{font-size:2.4rem;flex-shrink:0;line-height:1;filter:drop-shadow(0 0 12px rgba(0,255,136,.35))}
.ctf-pwned-text{flex:1;min-width:0}
.ctf-pwned-owned{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:clamp(13px,2.5vw,19px);color:#00ff88;letter-spacing:.14em;text-transform:uppercase;text-shadow:0 0 20px rgba(0,255,136,.45);animation:ctf-glow 3s ease infinite}
.ctf-pwned-sub{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:11px;color:#56657a;margin-top:6px}
.ctf-pwned-stats{display:flex;gap:2.2rem;flex-shrink:0}
.ctf-stat{text-align:center}
.ctf-stat-n{display:block;font-family:'Syne',system-ui,sans-serif;font-size:1.7rem;font-weight:800;color:#00ff88;line-height:1}
.ctf-stat-l{display:block;margin-top:4px;font-family:'Share Tech Mono',ui-monospace,monospace;font-size:9px;text-transform:uppercase;letter-spacing:.1em;color:#56657a}

.sp-wrap{max-width:900px;margin:1.2rem auto 0;padding:1.2rem 1.6rem;background:#0c1118;border:1px solid #1a2738;border-radius:3px}
.sp-hdr{display:flex;align-items:center;gap:9px;margin-bottom:1rem}
.sp-num{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:11px;color:#2e4a5a;flex-shrink:0}
.sp-title{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:#56657a}
.sp-rule{flex:1;height:1px;background:linear-gradient(90deg,#1a2738,transparent)}
.sp-total{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:10px;color:#2e4a5a;background:#070a0e;border:1px solid #1a2738;border-radius:10px;padding:2px 8px;white-space:nowrap}
.sp-search-wrap{position:relative;margin-bottom:10px}
.sp-search{width:100%;padding:7px 12px 7px 30px;background:#070a0e;border:1px solid #1a2738;border-radius:3px;font-family:'Share Tech Mono',ui-monospace,monospace;font-size:11px;color:#b8c4d0;outline:none;transition:border-color .18s}
.sp-search:focus{border-color:rgba(0,255,136,.4)}
.sp-search::placeholder{color:#2e4a5a}
.sp-tabs{display:flex;gap:0;border-bottom:1px solid #1a2738;margin-bottom:10px}
.sp-tab{display:inline-flex;align-items:center;gap:6px;font-family:'Share Tech Mono',ui-monospace,monospace;font-size:11px;letter-spacing:.07em;text-transform:uppercase;color:#56657a;padding:7px 16px;cursor:pointer;background:none;border:none;border-bottom:2px solid transparent;transition:all .18s}
.sp-tab:hover{color:#b8c4d0}
.sp-tab.active{color:#00ff88;border-bottom-color:#00ff88}
.sp-tab-cyan.active{color:#00d4ff !important;border-bottom-color:#00d4ff !important}
.sp-tab-n{font-size:9px;padding:1px 5px;border-radius:8px;background:rgba(0,255,136,.12);color:#00ff88;font-family:'Share Tech Mono',monospace}
.sp-tab-n-cyan{background:rgba(0,212,255,.12);color:#00d4ff}
.sp-body{display:flex;flex-direction:column;gap:8px;max-height:260px;overflow-y:auto;padding-right:4px}
.sp-body::-webkit-scrollbar{width:3px}
.sp-body::-webkit-scrollbar-track{background:transparent}
.sp-body::-webkit-scrollbar-thumb{background:#1a2738;border-radius:2px}
.sp-cat-label{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:9px;text-transform:uppercase;letter-spacing:.1em;color:#2e4a5a;margin-bottom:5px;display:flex;align-items:center;gap:6px}
.sp-cat-label::after{content:'';flex:1;height:1px;background:#1a2738}
.sp-tags{display:flex;flex-wrap:wrap;gap:5px}
.sp-tag{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:10.5px;letter-spacing:.05em;text-transform:uppercase;padding:3px 8px;border-radius:2px;border:0.5px solid;cursor:default;white-space:nowrap;transition:opacity .15s}
.sp-tag:hover{opacity:.75}
.sp-empty{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:11px;color:#2e4a5a;padding:1.2rem 0;text-align:center}

.t-web   {background:rgba(0,212,255,.07);border-color:rgba(0,212,255,.28);color:#00d4ff}
.t-inject{background:rgba(204,68,255,.07);border-color:rgba(204,68,255,.28);color:#cc44ff}
.t-privl {background:rgba(0,255,136,.07);border-color:rgba(0,255,136,.28);color:#00ff88}
.t-privw {background:rgba(0,180,100,.07);border-color:rgba(0,180,100,.28);color:#00b464}
.t-ad    {background:rgba(255,140,66,.07);border-color:rgba(255,140,66,.28);color:#ff8c42}
.t-net   {background:rgba(255,209,102,.07);border-color:rgba(255,209,102,.28);color:#ffd166}
.t-crypto{background:rgba(255,68,85,.07);border-color:rgba(255,68,85,.28);color:#ff4455}
.t-stego {background:rgba(230,60,75,.07);border-color:rgba(230,60,75,.28);color:#e63c4b}
.t-bin   {background:rgba(210,166,121,.07);border-color:rgba(210,166,121,.28);color:#d2a679}
.t-re    {background:rgba(180,142,173,.07);border-color:rgba(180,142,173,.28);color:#b48ead}
.t-cloud {background:rgba(86,182,194,.07);border-color:rgba(86,182,194,.28);color:#56b6c2}
.t-misc  {background:rgba(86,101,122,.07);border-color:rgba(86,101,122,.28);color:#56657a}
.t-recon {background:rgba(0,212,255,.07);border-color:rgba(0,212,255,.28);color:#00d4ff}
.t-exp   {background:rgba(204,68,255,.07);border-color:rgba(204,68,255,.28);color:#cc44ff}
.t-post  {background:rgba(0,255,136,.07);border-color:rgba(0,255,136,.28);color:#00ff88}
.t-lat   {background:rgba(255,140,66,.07);border-color:rgba(255,140,66,.28);color:#ff8c42}
.t-evas  {background:rgba(255,68,85,.07);border-color:rgba(255,68,85,.28);color:#ff4455}
.t-tunnel{background:rgba(86,182,194,.07);border-color:rgba(86,182,194,.28);color:#56b6c2}

.ctf-chain-section{max-width:900px;margin:1.5rem auto 0}
.ctf-sec-hdr{display:flex;align-items:center;gap:12px;margin-bottom:1.5rem}
.ctf-sec-num{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:12px;color:#2e4a5a;flex-shrink:0}
.ctf-sec-title{font-family:'Syne',system-ui,sans-serif;font-size:1.2rem;font-weight:800;color:#e2eaf3;letter-spacing:-.015em;flex-shrink:0;margin:0}
.ctf-sec-rule{flex:1;height:1px;background:linear-gradient(90deg,#1a2738,transparent)}
.ctf-attack-flow{position:relative;display:flex;flex-direction:column;padding-left:3.5rem}
.ctf-attack-flow::before{content:'';position:absolute;left:21px;top:8px;bottom:8px;width:1px;background:linear-gradient(180deg,rgba(0,255,136,.5),rgba(26,39,56,.3) 90%)}
.ctf-flow-step{position:relative;display:flex;align-items:flex-start;gap:1rem;padding:.9rem 0;border-bottom:1px solid rgba(26,39,56,.5)}
.ctf-flow-step:last-child{border-bottom:none}
.ctf-flow-num{position:absolute;left:-3.5rem;width:36px;height:36px;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-family:'Share Tech Mono',ui-monospace,monospace;font-size:11px;color:#00ff88;background:#060a0d;border:1px solid #1a2738;border-radius:50%;transition:border-color .2s,box-shadow .2s}
.ctf-flow-step:hover .ctf-flow-num{border-color:rgba(0,255,136,.4);box-shadow:0 0 14px rgba(0,255,136,.18)}
.ctf-flow-body{flex:1;min-width:0}
.ctf-flow-title-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:.3rem}
.ctf-flow-title{font-family:'Syne',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:#e2eaf3;letter-spacing:-.01em}
.ctf-flow-tag{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:10px;letter-spacing:.1em;text-transform:uppercase;padding:2px 8px;border-radius:2px;flex-shrink:0}
.ctf-flow-desc{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:12px;color:#56657a;line-height:1.72;margin:0}
.ctf-flow-desc code{font-family:'Share Tech Mono',ui-monospace,monospace;background:rgba(0,255,136,.08);border:1px solid rgba(0,255,136,.15);color:#00ff88;padding:1px 5px;border-radius:2px;font-size:11px}

.ctf-page{background:#070a0e;background-image:linear-gradient(rgba(0,255,136,.012) 1px,transparent 1px),linear-gradient(90deg,rgba(0,255,136,.012) 1px,transparent 1px);background-size:44px 44px;min-height:100vh;padding-bottom:7rem}
.ctf-layout{max-width:1080px;margin:0 auto;padding:3.5rem 2rem 0;display:grid;grid-template-columns:210px 1fr;gap:3.5rem;align-items:start}
@media(max-width:960px){.ctf-hero-shell{grid-template-columns:1fr}.ctf-hero-rail{max-width:520px}}
@media(max-width:880px){.ctf-layout{grid-template-columns:1fr}.ctf-toc{display:none}}

.ctf-toc{position:sticky;top:80px;font-family:'JetBrains Mono',ui-monospace,monospace}
.ctf-toc-header{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#56657a;margin-bottom:1rem}
.ctf-toc-prefix{color:#00ff88}
.ctf-toc-list{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:1px}
.ctf-toc-item{display:flex;align-items:center;gap:6px;font-size:12px;color:#56657a;text-decoration:none;padding:6px 10px;border-radius:3px;border-left:2px solid transparent;transition:all .18s;line-height:1.4}
.ctf-toc-item:hover{color:#00d4ff;background:rgba(0,212,255,.05)}
.ctf-toc-item.is-active{color:#00ff88 !important;border-left-color:#00ff88 !important;background:rgba(0,255,136,.06) !important}
.ctf-toc-n{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:10px;color:#2e4a5a;flex-shrink:0}
.ctf-toc-label{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ctf-toc-deco{display:flex;flex-direction:column;gap:3px;margin-top:1.6rem;padding-left:10px}
.ctf-toc-deco div{height:2px;border-radius:2px;background:#1a2738}
.ctf-toc-deco div:nth-child(1){width:40px}
.ctf-toc-deco div:nth-child(2){width:28px;opacity:.6}
.ctf-toc-deco div:nth-child(3){width:18px;opacity:.35}
.ctf-toc-deco div:nth-child(4){width:8px;opacity:.2}

.ctf-article{max-width:720px;min-width:0}
.ctf-prose{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:13.5px;line-height:1.85;color:#b8c4d0}
.ctf-p{margin-bottom:1.1rem}
.ctf-h2{font-family:'Syne',system-ui,sans-serif;font-size:1.55rem;font-weight:700;color:#e2eaf3;letter-spacing:-.015em;margin:3.2rem 0 1.2rem;padding-bottom:.7rem;border-bottom:1px solid #1a2738;display:flex;align-items:center;scroll-margin-top:90px}
.ctf-h2-anchor{text-decoration:none;display:inline-flex;align-items:center}
.ctf-h2-hash{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:.9rem;color:#00ff88;opacity:.55;letter-spacing:.04em}
.ctf-h3{font-family:'Syne',system-ui,sans-serif;font-size:1.1rem;font-weight:700;color:#e2eaf3;letter-spacing:-.01em;margin:2.2rem 0 .8rem;display:flex;align-items:center;gap:8px;scroll-margin-top:90px}
.ctf-h3-hash{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:.8rem;color:#00d4ff;opacity:.5;flex-shrink:0}
.ctf-blockquote{display:flex;gap:0;margin:1.6rem 0;border-radius:0 3px 3px 0;overflow:hidden;border:1px solid rgba(0,255,136,.12);border-left:none;background:rgba(0,255,136,.03)}
.ctf-bq-bar{width:3px;flex-shrink:0;background:linear-gradient(180deg,#00ff88,#00d4ff)}
.ctf-bq-body{padding:1rem 1.3rem;font-size:13px;color:#00cc6a;font-style:italic}
.ctf-bq-body p{margin:0}
.ctf-table-wrap{overflow-x:auto;margin:1.6rem 0;border:1px solid #1a2738;border-radius:3px}
.ctf-table{width:100%;border-collapse:collapse;font-size:12.5px}
.ctf-th{text-align:left;padding:9px 14px;font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:#56657a;background:#0c1118;border-bottom:1px solid #1a2738}
.ctf-td{padding:9px 14px;color:#b8c4d0;border-bottom:1px solid rgba(26,39,56,.6);vertical-align:top}
.ctf-tr:last-child .ctf-td{border-bottom:none}
.ctf-tr:hover .ctf-td{background:rgba(0,255,136,.025)}
.ctf-a{color:#00d4ff;text-decoration:none;border-bottom:1px dotted rgba(0,212,255,.4);transition:color .18s,border-color .18s}
.ctf-a:hover{color:#00ff88;border-color:rgba(0,255,136,.6)}
.ctf-inline-code,.ctf-prose :not(pre) > code{font-family:'Share Tech Mono',ui-monospace,monospace !important;background:rgba(0,255,136,.08);border:1px solid rgba(0,255,136,.16);color:#00ff88;padding:1px 6px;border-radius:3px;font-size:12px}
.ctf-ul,.ctf-ol{list-style:none;padding:0;margin:.4rem 0 1rem}
.ctf-li{display:flex;align-items:baseline;gap:9px;padding:3px 0;font-size:13.5px}
.ctf-li-bullet{color:#00ff88;font-size:10px;flex-shrink:0;margin-top:2px}
.ctf-divider{display:flex;align-items:center;gap:12px;margin:2.8rem 0}
.ctf-divider span:not(.ctf-divider-gem){flex:1;height:1px;background:linear-gradient(90deg,transparent,#1a2738)}
.ctf-divider span:first-child{background:linear-gradient(90deg,#1a2738,transparent)}
.ctf-divider-gem{color:#00ff88;font-size:11px;opacity:.45;flex-shrink:0}

/* FIX: images — no animation, will-change isolates repaint layer */
.ctf-img{width:100%;display:block;margin:1.6rem 0;border-radius:3px;border:1px solid #1a2738;cursor:zoom-in;transition:border-color .2s;will-change:transform;transform:translateZ(0)}
.ctf-img:hover{border-color:rgba(0,255,136,.3)}

/* FIX: Zoom wrapper — contain layout so it never causes reflow */
[data-rmiz-wrap="visible"],[data-rmiz-wrap="hidden"]{display:block !important;width:100% !important;contain:layout style}

/* Lessons Learned stands out in the table of contents */
.ctf-toc-item.is-lessons .ctf-toc-label{color:#00ff88;font-weight:600}

/* Prev / Next writeup pager */
.ctf-pager{max-width:860px;margin:2.6rem auto 0;padding:0 2rem;display:grid;grid-template-columns:1fr 1fr;gap:1rem}
.ctf-pager-card{display:flex;flex-direction:column;gap:8px;padding:1.1rem 1.3rem;border:1px solid #1a2738;border-radius:4px;background:rgba(12,17,24,.6);text-decoration:none;transition:border-color .2s,transform .2s}
.ctf-pager-card:hover{border-color:rgba(0,255,136,.4);transform:translateY(-2px)}
.ctf-pager-next{text-align:right}
.ctf-pager-next .ctf-pager-dir{justify-content:flex-end}
.ctf-pager-dir{display:inline-flex;align-items:center;gap:6px;font-family:'Share Tech Mono',ui-monospace,monospace;font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#56657a}
.ctf-pager-title{font-family:'Syne',system-ui,sans-serif;font-weight:700;font-size:15px;color:#e2eaf3;line-height:1.3}
.ctf-pager-card:hover .ctf-pager-title{color:#00ff88}
.ctf-pager-spacer{display:block}

/* author CTA (end of article) */
.ctf-author{display:flex;gap:1.1rem;align-items:flex-start;margin-top:3rem;padding:1.5rem 1.6rem;border:1px solid #1a2738;border-top:2px solid rgba(0,255,136,.4);border-radius:4px;background:linear-gradient(180deg,rgba(0,255,136,.04),rgba(12,17,24,.5))}
.ctf-author-photo{width:72px;height:72px;flex:none;border-radius:4px;object-fit:cover;border:1px solid rgba(0,255,136,.3);background:#0c1118}
.ctf-author-glyph{display:flex;align-items:center;justify-content:center;font-family:'Share Tech Mono',ui-monospace,monospace;font-size:22px;color:#00ff88;box-shadow:0 0 18px rgba(0,255,136,.1)}
.ctf-author-body{min-width:0}
.ctf-author-name{font-family:'Syne',system-ui,sans-serif;font-weight:800;font-size:18px;color:#e2eaf3;letter-spacing:-.01em}
.ctf-author-line{margin:.4rem 0 1rem;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:12.5px;line-height:1.7;color:#8ea0b4;max-width:60ch}
.ctf-author-actions{display:flex;flex-wrap:wrap;gap:8px}
.ctf-author-btn{display:inline-flex;align-items:center;gap:6px;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:11px;letter-spacing:.05em;text-transform:uppercase;padding:7px 13px;border:1px solid #1a2738;border-radius:3px;color:#8ea0b4;text-decoration:none;background:rgba(255,255,255,.02);transition:all .18s}
.ctf-author-btn:hover{border-color:rgba(0,255,136,.4);color:#00ff88;background:rgba(0,255,136,.05)}
.ctf-author-btn-primary{background:rgba(0,255,136,.11);border-color:rgba(0,255,136,.38);color:#00ff88}
.ctf-author-btn-primary:hover{background:rgba(0,255,136,.18)}

/* related writeups */
.ctf-related{max-width:900px;margin:3rem auto 0;padding:0 2rem}
.ctf-related-head{margin-bottom:1.2rem}
.ctf-related-kicker{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:#56657a}
.ctf-related-title{font-family:'Syne',system-ui,sans-serif;font-weight:800;font-size:clamp(1.2rem,3vw,1.6rem);letter-spacing:-.02em;color:#e2eaf3;margin:.4rem 0 0}
.ctf-related-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:1rem}
.ctf-related-card{display:flex;flex-direction:column;gap:.6rem;padding:1.2rem;border:1px solid #1a2738;border-radius:4px;background:rgba(12,17,24,.6);text-decoration:none;transition:border-color .2s,transform .2s}
.ctf-related-card:hover{border-color:rgba(0,255,136,.4);transform:translateY(-2px)}
.ctf-related-badges{display:flex;flex-wrap:wrap;gap:6px}
.ctf-related-chip{font-family:'Share Tech Mono',ui-monospace,monospace;font-size:9px;letter-spacing:.1em;text-transform:uppercase;padding:3px 8px;border:1px solid #1a2738;border-radius:2px;color:#8ea0b4}
.ctf-related-name{font-family:'Syne',system-ui,sans-serif;font-weight:700;font-size:15px;color:#e2eaf3;line-height:1.3;margin:0}
.ctf-related-card:hover .ctf-related-name{color:#00ff88}
.ctf-related-desc{margin:0;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:11.5px;line-height:1.6;color:#7f96ac;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.ctf-related-open{margin-top:auto;display:inline-flex;align-items:center;gap:5px;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:#00ff88}

@media(max-width:640px){
  .ctf-pager{grid-template-columns:1fr;padding:0 1.2rem}
  .ctf-pager-next{text-align:left}
  .ctf-pager-next .ctf-pager-dir{justify-content:flex-start}
  .ctf-related{padding:0 1.2rem}
  .ctf-author{flex-direction:column}
}

@media(max-width:600px){
  .ctf-hero-inner,.ctf-cover-wrap,.ctf-pwned-banner,
  .sp-wrap,.ctf-chain-section{padding-left:1.2rem;padding-right:1.2rem}
  .ctf-pwned-banner{margin-left:1.2rem;margin-right:1.2rem}
  .sp-wrap{margin-left:1.2rem;margin-right:1.2rem}
  .ctf-chain-section{margin-left:1.2rem;margin-right:1.2rem}
  .ctf-layout{padding:2rem 1.2rem 0}
  .ctf-pwned-stats{gap:1.4rem}
  .ctf-intel-head{flex-direction:column;align-items:flex-start}
  [data-rmiz-btn-open],[data-rmiz-btn-close]{display:none !important}
}
`;

export default WriteupPage;
